import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithReAuth } from './base-query'
import type { OkResponse } from './types/common'
import type {
	AvitoOauthUrlResponse,
	CompleteAvitoOauthBody,
	CompleteImportBody,
	ImportAvitoItemsBody,
	ImportAvitoItemsResponse,
	ListAvitoItemsParams,
	ListAvitoItemsResponse,
	ConnectPlatformBody,
	ConnectTelegramBotBody,
	ConnectPlatformResponse,
	ExternalListing,
	FeedUrlResponse,
	GetAvitoOauthUrlParams,
	IntegrationPlatform,
	ListConnectionsResponse,
	ListExternalListingsParams,
	ListExternalListingsResponse,
	PlatformConnection,
	PublicationImport,
	PublishListingBody,
	PublishListingResponse,
	StartImportBody,
} from './types/integration'

export const integrationApi = createApi({
	reducerPath: 'integrationApi',
	baseQuery: baseQueryWithReAuth,
	tagTypes: ['Connection', 'ExternalListing', 'PublicationImport', 'AvitoItem'],
	endpoints: (builder) => ({
		// ── Connections ──────────────────────────────────────

		listConnections: builder.query<ListConnectionsResponse, void>({
			query: () => '/integration/connections',
			providesTags: ['Connection'],
		}),

		getConnection: builder.query<{ connection: PlatformConnection | null }, IntegrationPlatform>({
			query: (platform) => `/integration/connections/${platform}`,
			providesTags: (_r, _e, platform) => [{ type: 'Connection', id: platform }],
		}),

		connectPlatform: builder.mutation<ConnectPlatformResponse, ConnectPlatformBody>({
			query: (body) => ({ url: '/integration/connect', method: 'POST', body }),
			invalidatesTags: ['Connection'],
		}),

		disconnectPlatform: builder.mutation<OkResponse, IntegrationPlatform>({
			query: (platform) => ({
				url: `/integration/disconnect/${platform}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Connection'],
		}),

		connectTelegramBot: builder.mutation<PlatformConnection, ConnectTelegramBotBody>({
			query: (body) => ({ url: '/integration/telegram/connect', method: 'POST', body }),
			invalidatesTags: ['Connection'],
		}),

		// ── External Listings ────────────────────────────────

		listExternalListings: builder.query<
			ListExternalListingsResponse,
			ListExternalListingsParams | void
		>({
			query: (params) => ({ url: '/integration/listings', params: params ?? {} }),
			providesTags: ['ExternalListing'],
		}),

		publishListing: builder.mutation<PublishListingResponse, PublishListingBody>({
			query: (body) => ({ url: '/integration/publish', method: 'POST', body }),
			invalidatesTags: ['ExternalListing'],
		}),

		unpublishListing: builder.mutation<OkResponse, PublishListingBody>({
			query: (body) => ({ url: '/integration/unpublish', method: 'DELETE', body }),
			invalidatesTags: ['ExternalListing'],
		}),

		syncListing: builder.mutation<ExternalListing | null, string>({
			query: (listingId) => ({
				url: `/integration/listings/${listingId}/sync`,
				method: 'POST',
			}),
			invalidatesTags: ['ExternalListing'],
		}),

		// ── Feed ─────────────────────────────────────────────

		getFeedUrl: builder.query<FeedUrlResponse, IntegrationPlatform>({
			query: (platform) => `/integration/feed-url/${platform}`,
		}),

		// ── Imports ──────────────────────────────────────────

		startImport: builder.mutation<PublicationImport, StartImportBody>({
			query: (body) => ({ url: '/integration/imports', method: 'POST', body }),
			invalidatesTags: ['PublicationImport'],
		}),

		getImport: builder.query<PublicationImport, string>({
			query: (id) => `/integration/imports/${id}`,
			providesTags: (_r, _e, id) => [{ type: 'PublicationImport', id }],
		}),

		completeImport: builder.mutation<
			PublicationImport,
			{ id: string; body: CompleteImportBody }
		>({
			query: ({ id, body }) => ({
				url: `/integration/imports/${id}/complete`,
				method: 'POST',
				body,
			}),
			invalidatesTags: (_r, _e, { id }) => [{ type: 'PublicationImport', id }],
		}),

		// ── Avito OAuth ─────────────────────────────────────

		getAvitoOauthUrl: builder.query<AvitoOauthUrlResponse, GetAvitoOauthUrlParams | void>({
			query: (params) => ({ url: '/integration/avito/oauth/url', params: params ?? {} }),
		}),

		completeAvitoOauth: builder.mutation<PlatformConnection, CompleteAvitoOauthBody>({
			query: (body) => ({ url: '/integration/avito/oauth/callback', method: 'POST', body }),
			invalidatesTags: ['Connection'],
		}),

		// ── Avito cabinet items (Avito → system import) ──────

		listAvitoItems: builder.query<ListAvitoItemsResponse, ListAvitoItemsParams>({
			query: ({ connectionId, ...params }) => ({
				url: `/integration/avito/connections/${connectionId}/items`,
				params,
			}),
			providesTags: ['AvitoItem'],
		}),

		importAvitoItems: builder.mutation<
			ImportAvitoItemsResponse,
			ImportAvitoItemsBody
		>({
			query: ({ connectionId, avitoItemIds }) => ({
				url: `/integration/avito/connections/${connectionId}/items/import`,
				method: 'POST',
				body: { avitoItemIds },
			}),
			invalidatesTags: ['AvitoItem', 'ExternalListing'],
		}),

	}),
})

export const {
	useListConnectionsQuery,
	useGetConnectionQuery,
	useConnectPlatformMutation,
	useConnectTelegramBotMutation,
	useDisconnectPlatformMutation,
	useListExternalListingsQuery,
	usePublishListingMutation,
	useUnpublishListingMutation,
	useSyncListingMutation,
	useGetFeedUrlQuery,
	useStartImportMutation,
	useGetImportQuery,
	useCompleteImportMutation,
	useGetAvitoOauthUrlQuery,
	useLazyGetAvitoOauthUrlQuery,
	useCompleteAvitoOauthMutation,
	useListAvitoItemsQuery,
	useImportAvitoItemsMutation,
} = integrationApi
